import { useEffect, useMemo, useState } from "react";
import useVariations from "./hooks/useVariations";
import useVariationsFilter from "./hooks/useVariationsFilter";
import useLang from "../../hooks/useLang";
import useConfigurationPage from "./hooks/useConfigurationPage";
import useSearchParams from "../../hooks/useSearchParams";
import { PageHeader, SectionHeader } from "../../components/Header";
import Button from "../../components/Button";
import Section from "../../components/Section";
import Parameters from "./Parameters";
import ConstantsBlock from "./ConstantsBlock";
import PriceFormulaBlock from "./formula/PriceFormulaBlock";
import Variations from "./Variations";
import VariationsFilter from "./VariationsFilter";
import VariationBlock from "./VariationBlock";
import { FormModalControlled } from "../../components/Modal";
import { useSelector, useDispatch } from "react-redux";
import {
  selectConfigurations,
  selectColorSuffixes,
  selectDefaultVariation,
  selectWizardParameters,
  selectWizardSelections,
  setDefaultVariation,
  setWizardStepSelection,
  addWizardParameter,
  removeWizardParameter,
  toggleParameter,
  AVAILABLE_PARAMETERS,
} from "./configurationsSlice";
import getVariationIdFromCombo from "./utils/getVariationIdFromCombo";
import FormulaHistoryModal from "./formula/FormulaHistoryModal";
import DropdownSelector from "../../components/DropdownSelector";
import Input from "../../components/Input";
import Label from "../../components/Label";

const WIDTH_ID = "WIDTH";
const uniq = (arr) => Array.from(new Set(arr));

const PARAM_LABEL_TO_ID = { Plotis: "WIDTH", Spalva: "COLOR" };

const PARAM_TYPE_OPTIONS = [
  { id: "NUMBER", label: "Skaičius" },
  { id: "NUMBER_SEQ", label: "Skaičių seka" },
  { id: "TEXT", label: "Tekstas" },
  { id: "COLOR", label: "Spalva" },
  { id: "WIZARD", label: "Vedlys" },
];

const PARAM_TYPE_MAP = {
  WIDTH: PARAM_TYPE_OPTIONS.find((o) => o.id === "NUMBER"),
  COLOR: PARAM_TYPE_OPTIONS.find((o) => o.id === "COLOR"),
};

const ParametersDialog = ({ isOpen, onClose, configurationId, onRemoveHotspot }) => {
  const dispatch = useDispatch();
  const configurations = useSelector(selectConfigurations);
  const config = configurations.find((c) => c.id === configurationId);
  const activeParamIds = (config?.parameters || []).map(
    (label) => PARAM_LABEL_TO_ID[label],
  );
  const wizardParams = useSelector(selectWizardParameters(configurationId));

  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState(PARAM_TYPE_OPTIONS[4]); // Vedlys default

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    gap: "var(--gap)",
    padding: "var(--spacing-2) 0",
  };

  const handleAdd = () => {
    if (!newName.trim()) return;
    if (newType.id === "WIZARD" || newType.id === "NUMBER_SEQ") {
      dispatch(
        addWizardParameter({
          configurationId,
          label: newName.trim(),
          paramType: newType.id,
        }),
      );
      setNewName("");
    }
  };

  return (
    <FormModalControlled
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onClose}
      onCancel={onClose}
      submitLabel="Gerai"
      cancelLabel="Atšaukti"
    >
      <h2>Parametrų sąrašas</h2>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--spacing-2)",
          margin: "var(--gap) 0",
          minWidth: "min(560px, 90vw)",
        }}
      >
        {AVAILABLE_PARAMETERS.map((param) => {
          const isActive = activeParamIds.includes(param.id);
          const typeValue = PARAM_TYPE_MAP[param.id] || PARAM_TYPE_OPTIONS[0];

          return (
            <div key={param.id} style={rowStyle}>
              <div style={{ width: "var(--width-20)", flexShrink: 0 }}>
                <Label>{param.label}</Label>
              </div>
              <Input
                type="checkbox"
                label="Sukurti variaciją"
                name={`param_active_${param.id}`}
                value={isActive}
                onChange={() => {
                  if (isActive) {
                    onRemoveHotspot?.(param.id);
                  }
                  dispatch(
                    toggleParameter({
                      configurationId,
                      parameterId: param.id,
                    }),
                  );
                }}
              />
              <div style={{ width: "var(--width-32)" }}>
                <DropdownSelector
                  options={PARAM_TYPE_OPTIONS}
                  value={typeValue}
                  noEmpty
                  onChange={() => {}}
                />
              </div>
              <Button inline icon="trashIcon" disabled>
                Ištrinti
              </Button>
            </div>
          );
        })}

        {wizardParams.map((wizard) => (
          <div key={wizard.id} style={rowStyle}>
            <div style={{ width: "var(--width-20)", flexShrink: 0 }}>
              <Label>{wizard.label}</Label>
            </div>
            <Input
              type="checkbox"
              label="Sukurti variaciją"
              name={`param_active_${wizard.id}`}
              value={false}
              disabled
            />
            <div style={{ width: "var(--width-32)" }}>
              <DropdownSelector
                options={PARAM_TYPE_OPTIONS}
                value={PARAM_TYPE_OPTIONS.find((o) => o.id === (wizard.type || "WIZARD")) || PARAM_TYPE_OPTIONS[4]}
                noEmpty
                onChange={() => {}}
              />
            </div>
            <Button
              inline
              icon="trashIcon"
              onClick={() => {
                dispatch(
                  removeWizardParameter({
                    configurationId,
                    wizardId: wizard.id,
                  }),
                );
                onRemoveHotspot?.(wizard.id);
              }}
            >
              Ištrinti
            </Button>
          </div>
        ))}

        <div
          style={{
            borderTop: "1px solid var(--clr-grey-300)",
            paddingTop: "var(--gap)",
            marginTop: "var(--spacing-2)",
            ...rowStyle,
          }}
        >
          <div style={{ width: "var(--width-20)", flexShrink: 0 }}>
            <Label>Pavadinimas</Label>
          </div>
          <div style={{ flexGrow: 1 }}>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder=""
            />
          </div>
          <div style={{ width: "var(--width-32)" }}>
            <DropdownSelector
              options={PARAM_TYPE_OPTIONS}
              value={newType}
              noEmpty
              onChange={(next) => setNewType(next)}
            />
          </div>
          <Button
            inline
            icon="plus"
            emphasized
            onClick={handleAdd}
            disabled={!newName.trim()}
          >
            Pridėti
          </Button>
        </div>
      </div>
    </FormModalControlled>
  );
};

const ConfigurationPage = () => {
  const lang = useLang();
  const dispatch = useDispatch();
  const {
    configurationId,
    activeVariationId,
    parameters,
    constantsArray,
    onConstantsChange,
    defaultVariationId: defaultVariationIdFromHook,
    hotspots,
    hotspotsList,
    onHotspotDrag,
    tokens,
    onFormulaChange,
    formulaHistory,
    restoreFormula,
    getPrice: getBasePrice,
    disableChange,
    onDeleteParamValue,
    showParametersDialog,
    setShowParametersDialog,
    setInitialHotspots,
    moveHotspots,
    removeHotspotById,
  } = useConfigurationPage();

  const reduxDefaultVariationId = useSelector(selectDefaultVariation(configurationId));
  const defaultVariationId = reduxDefaultVariationId || defaultVariationIdFromHook;
  const wizardParams = useSelector(selectWizardParameters(configurationId));
  const wizardSelections = useSelector(selectWizardSelections(configurationId));

  const { onFilterChange, getFilteredVariations } = useVariationsFilter();

  const [widthExtraValues, setWidthExtraValues] = useState([]);
  const [widthModelsByValue, setWidthModelsByValue] = useState({});
  const [colorExtraValues, setColorExtraValues] = useState([]);
  const [condition, setCondition] = useState({ ifTokens: [], thenTokens: [] });
  const [showHistory, setShowHistory] = useState(false);

  // parameters here is from useConfigurationPage() and is an ARRAY for the current config
  const mergedParameters = useMemo(() => {
    return (parameters || []).map((p) => {
      if (p.id === WIDTH_ID) {
        return { ...p, values: uniq([...(p.values || []), ...widthExtraValues]) };
      }
      if (p.id === "COLOR") {
        return { ...p, values: uniq([...(p.values || []), ...colorExtraValues]) };
      }
      return p;
    });
  }, [parameters, widthExtraValues, colorExtraValues]);

  // Merge wizard steps into parameter list for formula/condition use
  const allParametersForFormula = useMemo(() => {
    const wizardStepParams = (wizardParams || []).flatMap((wp) =>
      wp.steps.map((step) => ({
        id: step.id,
        label: step.label,
        values: step.options,
      })),
    );
    return [...(mergedParameters || []), ...wizardStepParams];
  }, [mergedParameters, wizardParams]);

  // Wrap getPrice to apply dynamic conditions
  const getPrice = ({ parameters: params }) => {
      const base = getBasePrice({ parameters: params });
      if (base.error) return base;
      const safeParams = params || [];

      const { ifTokens, thenTokens } = condition;
      if (!ifTokens?.length || !thenTokens?.length) return base;

      // Map parameter labels to variation keys
      const labelToKey = {};
      (mergedParameters || []).forEach((p) => {
        labelToKey[p.label?.toLowerCase()] = p.id?.toLowerCase();
        labelToKey[p.id?.toLowerCase()] = p.id?.toLowerCase();
      });
      // Also map wizard step labels
      (wizardParams || []).forEach((wp) => {
        wp.steps.forEach((step) => {
          labelToKey[step.label?.toLowerCase()] = step.id;
        });
      });

      const getParamValue = (paramLabel) => {
        const key = labelToKey[paramLabel?.toLowerCase()] || paramLabel?.toLowerCase();
        const p = safeParams.find(
          (param) => Object.keys(param)[0]?.toLowerCase() === key,
        );
        if (p) return Object.values(p)[0];
        if (wizardSelections[key]) return wizardSelections[key];
        for (const wp of wizardParams || []) {
          const step = wp.steps.find((s) => s.id === key);
          if (step) return wizardSelections[step.id] || step.options[0];
        }
        return undefined;
      };

      let conditionResult = null;
      let currentParam = null;
      let currentOp = null;
      let pendingLogic = null;

      for (const token of ifTokens) {
        if (token.type === "PARAMETER") {
          currentParam = token.value;
        } else if (token.type === "OPERATOR") {
          currentOp = token.value;
        } else if (token.type === "VALUE") {
          if (currentParam != null && currentOp != null) {
            const actual = getParamValue(currentParam);
            const expected = token.value;
            let match = false;
            if (currentOp === "==") match = String(actual) === String(expected);
            else if (currentOp === "!=") match = String(actual) !== String(expected);

            if (conditionResult === null) {
              conditionResult = match;
            } else if (pendingLogic === "IR") {
              conditionResult = conditionResult && match;
            } else if (pendingLogic === "ARBA") {
              conditionResult = conditionResult || match;
            }
            currentParam = null;
            currentOp = null;
            pendingLogic = null;
          }
        } else if (token.type === "LOGIC") {
          pendingLogic = token.value;
        }
      }

      if (!conditionResult) return base;

      // Build THEN expression string and evaluate
      let thenError = false;
      const thenExpression = thenTokens
        .map((token) => {
          if (token.type === "CONSTANT" && token.value === "Kaina") {
            return base.result;
          }
          if (token.type === "PARAMETER") {
            const val = getParamValue(token.value);
            if (val == null || Number.isNaN(Number(val))) {
              thenError = true;
              return 0;
            }
            return Number(val);
          }
          if (token.type === "NUMBER") {
            const v = String(token.value).trim();
            if (v === "" || Number.isNaN(Number(v))) thenError = true;
            return v;
          }
          if (token.type === "BRACKET") return token.value;
          if (token.type === "OPERATOR") return token.value;
          return token.value;
        })
        .join(" ");

      if (thenError) return base;

      try {
        // eslint-disable-next-line no-new-func
        const result = Function(`"use strict"; return (${thenExpression});`)();
        return { result };
      } catch {
        return base;
      }
  };

  const colorSuffixes = useSelector(selectColorSuffixes(configurationId));

  // IMPORTANT: compute variations from mergedParameters (ARRAY)
  const { variations, togglePrice, setManualPrice, deleteImage } =
    useVariations({
      parameters: { [configurationId]: mergedParameters }, // keep hook signature unchanged
      configurationId,
      activeVariationId,
      extraSuffixes: colorSuffixes,
    });

  const { setParam, removeParam } = useSearchParams();

  // Initialize hotspots for merged variations (includes extra width/color values)
  useEffect(() => {
    if (variations?.length > 0) {
      setInitialHotspots({ variations, wizardParameters: wizardParams });
    }
    // eslint-disable-next-line
  }, [variations ? JSON.stringify(variations.map((v) => ({ id: v.id, img: !!v.img }))) : "", JSON.stringify(wizardParams)]);

  // Auto-select first variation when none is selected, or clear if no variations
  useEffect(() => {
    if (!variations?.length) {
      if (activeVariationId) {
        removeParam("var");
      }
      return;
    }
    if (!variations.find((v) => v.id === activeVariationId)) {
      setParam("var", variations[0].id);
    }
    // eslint-disable-next-line
  }, [variations, activeVariationId]);

  // Auto-set default variation to first when none matches
  useEffect(() => {
    if (
      variations?.length > 0 &&
      !variations.find((v) => v.id === defaultVariationId)
    ) {
      dispatch(
        setDefaultVariation({
          configurationId,
          variationId: variations[0].id,
        }),
      );
    }
    // eslint-disable-next-line
  }, [variations, defaultVariationId]);

  const filteredVariations = getFilteredVariations(variations);
  const activeVariation = variations?.find((v) => v.id === activeVariationId);
  const manualPrice = activeVariation?.manualPrice;

  const handleDuplicateHotspotCoords = () => {
    const visibleIds = filteredVariations.map((v) => v.id);
    const active = hotspotsList?.[activeVariationId] ?? [];
    const payload = Object.fromEntries(visibleIds.map((id) => [id, active]));
    moveHotspots(payload);
  };

  const handleDefaultClick = () => {
    dispatch(
      setDefaultVariation({
        configurationId,
        variationId: activeVariationId,
      }),
    );
  };

  const handleManualPriceClick = () => {
    const variationPrice = getPrice({
      parameters: activeVariation?.parameters,
    });
    togglePrice({ ...activeVariation, price: variationPrice?.result });
  };

  const handlePriceChange = (data) => {
    setManualPrice({ ...activeVariation, price: data });
  };

  const handleImageDelete = () => {
    deleteImage(activeVariation);
  };

  const handleValueAdded = (paramId, newValue) => {
    // Build a combo using current selected values but overriding the changed param
    const combo = mergedParameters
      .map((p) => {
        if (p.id === paramId) return newValue;
        // Use currently active value for this param, or first available
        const activeParam = activeVariation?.parameters?.find(
          (vp) => Object.keys(vp)[0] === p.id?.toLowerCase(),
        );
        const currentVal = activeParam ? Object.values(activeParam)[0] : null;
        if (currentVal != null) return currentVal;
        return p.values?.[0] ?? null;
      })
      .filter((v) => v != null);
    if (combo.length > 0) {
      const newVarId = getVariationIdFromCombo(configurationId, combo, colorSuffixes);
      setParam("var", newVarId);
    }
  };

  return (
    <>
      <PageHeader>
        <h1>{configurationId}</h1>
      </PageHeader>
      <div
        style={{
          display: "flex",
          flexGrow: "1",
          justifyContent: "flex-end",
          paddingRight: "var(--horizontal-padding)",
          gap: "var(--gap)",
        }}
      >
        <Button important disabled={disableChange}>
          Išsaugoti
        </Button>
      </div>
      <Section noSeparator>
        <SectionHeader>
          <h2>{lang("parameters")}</h2>
          <div style={{ width: "var(--width-12)" }} />
          <Button
            inline
            emphasized
            disabled={disableChange}
            icon={"pencil"}
            onClick={() => {
              setShowParametersDialog(true);
            }}
          >
            Redaguoti sąrašą
          </Button>
          <div style={{ flexGrow: "1" }} />
        </SectionHeader>
        <Parameters
          parameters={mergedParameters}
          onDeleteParamValue={onDeleteParamValue}
          disableChange={disableChange}
          widthExtraValues={widthExtraValues}
          setWidthExtraValues={setWidthExtraValues}
          widthModelsByValue={widthModelsByValue}
          setWidthModelsByValue={setWidthModelsByValue}
          colorExtraValues={colorExtraValues}
          setColorExtraValues={setColorExtraValues}
          onValueAdded={handleValueAdded}
        />
      </Section>
      <Section noSeparator>
        <SectionHeader>
          <h2>{lang("constants")}</h2>
          <div style={{ width: "var(--width-12)" }} />
          <Button inline emphasized disabled={disableChange} icon={"pencil"}>
            Redaguoti sąrašą
          </Button>
          <div style={{ flexGrow: "1" }} />
        </SectionHeader>
        <ConstantsBlock
          constants={constantsArray}
          onChange={onConstantsChange}
        />
      </Section>
      <Section noSeparator>
        <SectionHeader>
          <h2>{lang("price_calculation")}</h2>
          <div style={{ width: "var(--width-12)" }} />
          <div style={{ width: "var(--width-4)" }} />
          <div style={{ width: "var(--width-4)" }} />
          <Button inline emphasized disabled={disableChange} icon={"plus"}>
            Pridėti sąlygą
          </Button>
          <div style={{ width: "var(--width-4)" }} />
          <Button inline icon={"description"} onClick={() => setShowHistory(true)}>
            Istorija
          </Button>
          <div style={{ flexGrow: "1" }} />
        </SectionHeader>
      </Section>
      <PriceFormulaBlock
        constants={constantsArray}
        tokens={tokens}
        onChange={onFormulaChange}
        parameters={activeVariation?.parameters}
        configParameters={allParametersForFormula}
        getPrice={getPrice}
        disableChange={disableChange}
        onConditionChange={setCondition}
      />
      <Section noSeparator>
        <SectionHeader>
          <h2>{`${lang("variation")}${activeVariationId ? ` (${activeVariationId})` : ""}`}</h2>
        </SectionHeader>
        <div>
          <VariationBlock
            variation={activeVariation}
            defaultVariationId={defaultVariationId}
            hotspots={hotspots}
            onHotspotDrag={onHotspotDrag}
            onDuplicateHotspotCoords={handleDuplicateHotspotCoords}
            getPrice={getPrice}
            manualPrice={manualPrice}
            onDefaultToggle={handleDefaultClick}
            onPriceToggle={handleManualPriceClick}
            disableChange={disableChange}
            onPriceChange={handlePriceChange}
            onImageDelete={handleImageDelete}
            wizardParameters={wizardParams}
            wizardSelections={wizardSelections}
            onWizardSelectionChange={(stepId, value) =>
              dispatch(
                setWizardStepSelection({
                  configurationId,
                  stepId,
                  value,
                }),
              )
            }
          />
        </div>
      </Section>
      <Section noSeparator>
        <SectionHeader>
          <h2>{`${lang("variation_list")}${
            variations && variations.length
              ? " (" + variations.length + ")"
              : ""
          }`}</h2>
          <div style={{ display: "flex", flexGrow: "1" }} />
          <VariationsFilter
            parameters={mergedParameters}
            onFilterChange={onFilterChange}
          />
        </SectionHeader>
        <Variations
          defaultVariationId={defaultVariationId}
          variations={filteredVariations}
          hotspots={hotspotsList}
          getPrice={getPrice}
        />
      </Section>
      <ParametersDialog
        isOpen={showParametersDialog}
        onClose={() => setShowParametersDialog(false)}
        configurationId={configurationId}
        onRemoveHotspot={removeHotspotById}
      />
      <FormulaHistoryModal
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        history={formulaHistory}
        constants={constantsArray}
        onRestore={restoreFormula}
      />
    </>
  );
};

export default ConfigurationPage;
